import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { Component, Prop, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { initialTextTemplatesGridState, TextTemplateGridState } from '@/modules/configurationModule/detail/templates/text-templates/components/text-templates-grid/text-templates-grid.models'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { textTemplatesConfigurationServices } from '@/modules/configurationModule/detail/templates/text-templates/services/text-templates.services'
import TextTemplatesModal from '@/modules/configurationModule/detail/templates/text-templates/components/text-templates-modal/text-templates-modal.vue'
import i18n from '@/lang/i18n'
import { ConfigurationTextTemplateResponse } from '@/modules/configurationModule/detail/templates/text-templates/services/text-templates.dto'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { TEXT_TEMPLATES_UPDATE_CHANNEL } from '@/modules/configurationModule/detail/templates/shared/models/templates.models'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        TextTemplatesModal
    }
})
export default class TextTemplatesGrid extends BaseCrudServerSideComponent<ConfigurationTextTemplateResponse> implements BaseCrud {
    @Ref() readonly textTemplatesModalWrapper!: any
    @Prop() readonly id!: number

    state: TextTemplateGridState
    constructor() {
        super()
        this.state = initialTextTemplatesGridState()
    }

    async mounted() {
        if (this.id) {
            this.state.broadcastChannel = new BroadcastChannel(TEXT_TEMPLATES_UPDATE_CHANNEL)
            const textTemplateResponse = await this.getConfigurationTextTemplateById(this.id)
            this.showModal(this.$tc('configuration.actions.update'), textTemplateResponse, this.textTemplatesModalWrapper)
        }
    }

    beforeDestroy() {
        this.state.broadcastChannel?.close()
    }

    get broadcastChannelPostMessageFunction(): Function {
        return () => this.state.broadcastChannel?.postMessage('textTemplatesUpdateChannel')
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:ConfigurationTextTemplateResponse = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.textTemplatesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async getConfigurationTextTemplateById(textTemplateId: number) {
        return this.$_toCallUnique(textTemplatesConfigurationServices.getTextTemplate(this.state.labId, textTemplateId))
    }
    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationTextTemplateResponse[]) {
        const templatesToPatch = selectionBatch?.filter((template: ConfigurationTextTemplateResponse) => template.active !== option)?.map((template:ConfigurationTextTemplateResponse) => {
            return {
                ...template,
                active: option
            }
        })
        if (templatesToPatch.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(textTemplatesConfigurationServices.patchTextTemplatesBatch(templatesToPatch, this.state.labId), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }
    async createOperation(template: ConfigurationTextTemplateResponse) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(textTemplatesConfigurationServices.createTextTemplates(template, this.state.labId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.textTemplatesModalWrapper.$slots.default[0].componentInstance.updateRow(response)
        this.state = {
            ...this.state,
            actionTitle: i18n.t('configuration.actions.update').toString()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }
    async patchOperation(template: ConfigurationTextTemplateResponse[]) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(textTemplatesConfigurationServices.patchTextTemplatesBatch(template, this.state.labId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }
    async onTextTemplatesModalUpdate(template: ConfigurationTextTemplateResponse[]) {
        await this.patchOperation(template)
        if (this.state.broadcastChannel) {
            this.broadcastChannelPostMessageFunction()
        }
    }
    async deleteOperation(templates: ConfigurationTextTemplateResponse[]) {
        if (templates.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this.deleteBatchOperation(templates)
            }
        }
    }
    async deleteBatchOperation(templates: ConfigurationTextTemplateResponse[]) {
        const templatesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: templates.map((template: ConfigurationTextTemplateResponse) => Number(template?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(textTemplatesConfigurationServices.deleteTextTemplatesBatch(templatesToDelete, this.state.labId), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (templates.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: templates?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
